import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Trash2, Loader2, FileCheck, BookOpen, Settings, Calendar, ChevronRight, Search, X, BarChart3 } from 'lucide-react'
import { rfpApi, dealsApi } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'
import { Combobox } from '@components/ui/Combobox'
import type { RfpProject, QualificationChecklist, RfpStage } from './types'
import { QUALIFICATION_CRITERIA, EMPTY_CHECKLIST, RFP_PRODUCTS, RFP_STAGES, STAGE_CONFIG } from './types'
import { RfpProjectDetail } from './components/RfpProjectDetail'
import { QualificationGuide } from './components/QualificationGuide'
import { RfpSettings } from './components/RfpSettings'
import { RfpAiPerformance } from './components/RfpAiPerformance'

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function unwrapList(res: any): any[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

function formatShortDate(dateStr: string | null) {
  if (!dateStr) return null
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

interface DealOption {
  id: number
  customer_name: string
  hubspot_deal_name?: string
}

function getQualificationProgress(project: RfpProject): { percent: number; count: number; total: number } {
  const cl = { ...EMPTY_CHECKLIST, ...project.qualification_checklist }
  const items = [
    !!project.question_cutoff_date,
    !!project.response_due_date,
    QUALIFICATION_CRITERIA.every(c => cl[c.key] !== null),
    (cl.competitors || '').trim().length > 0 && ((cl.current_vendor || '').trim().length > 0 || (cl.current_solution || '').trim().length > 0),
    cl.presales_recommendation !== null,
    !!project.original_filename,
  ]
  const count = items.filter(Boolean).length
  return { percent: Math.round((count / items.length) * 100), count, total: items.length }
}

export function RfpTool() {
  const { user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState<RfpProject[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDealId, setNewDealId] = useState<string>('')
  const [dealOptions, setDealOptions] = useState<DealOption[]>([])
  const [dealSearching, setDealSearching] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showPerformance, setShowPerformance] = useState(false)
  const [guideChecklist, setGuideChecklist] = useState<QualificationChecklist>(EMPTY_CHECKLIST)
  const [searchQuery, setSearchQuery] = useState('')
  const [stageFilter, setStageFilter] = useState<RfpStage | 'all'>('all')

  const selectProject = (project: RfpProject | null) => {
    if (project) {
      setSelectedId(project.id)
      setSearchParams({ rfp: slugify(project.title) }, { replace: true })
    } else {
      setSelectedId(null)
      setSearchParams({}, { replace: true })
    }
  }

  const loadAll = async () => {
    setLoading(true)
    const projRes = await rfpApi.listProjects()
    const loaded = unwrapList(projRes) as RfpProject[]
    setProjects(loaded)

    // Resolve URL slug to project ID on initial load
    const rfpSlug = searchParams.get('rfp')
    if (rfpSlug && !selectedId) {
      const match = loaded.find(p => slugify(p.title) === rfpSlug)
      if (match) setSelectedId(match.id)
    }

    setLoading(false)
  }

  const searchDeals = async (query: string) => {
    setDealSearching(true)
    const res = await dealsApi.list({ search: query || undefined, pageSize: 30 })
    if (res.success && res.data) {
      const raw = res.data as any
      const dealData = raw.items || raw.deals || raw.data?.items || raw.data?.deals || []
      if (Array.isArray(dealData)) setDealOptions(dealData)
    }
    setDealSearching(false)
  }

  useEffect(() => {
    loadAll()
  }, [])

  const handleCreate = async () => {
    setCreating(true)
    const res = await rfpApi.createProject({
      title: newTitle || 'Untitled RFP',
      deal_id: newDealId ? parseInt(newDealId) : undefined,
      created_by: user?.email || undefined,
      created_by_name: user?.name || undefined,
    })
    const data = (res?.data as any)?.data || res?.data
    if (data) {
      const created = data as RfpProject
      selectProject({ ...created, title: newTitle || 'Untitled RFP' } as RfpProject)
      setShowCreate(false)
      setNewTitle('')
      setNewDealId('')
    }
    setCreating(false)
  }

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    await rfpApi.deleteProject(id)
    setProjects(projects.filter(p => p.id !== id))
    showToast('RFP project deleted', 'success')
  }

  if (showPerformance) {
    return <RfpAiPerformance onBack={() => setShowPerformance(false)} />
  }

  if (selectedId) {
    return <RfpProjectDetail projectId={selectedId} onBack={() => { selectProject(null); loadAll() }} />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">RFP Tool</h2>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            Manage RFP/RFI projects, track qualification, and generate AI-powered responses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPerformance(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <BarChart3 size={14} />
            AI Performance
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <Settings size={14} />
            Settings
          </button>
          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <BookOpen size={14} />
            Qualification Guide
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
          >
            <Plus size={14} />
            New RFP Project
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Create RFP Project</h3>
          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
              placeholder="e.g. Acme Corp Security RFP 2026"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Link to Deal (optional)</label>
            <Combobox
              options={dealOptions.map(d => ({
                value: String(d.id),
                label: `${d.customer_name}${d.hubspot_deal_name ? ` — ${d.hubspot_deal_name}` : ''}`,
              }))}
              value={newDealId}
              onChange={(val) => setNewDealId(val)}
              onSearch={searchDeals}
              searching={dealSearching}
              placeholder="Search deals..."
              inputClassName="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowCreate(false); setNewTitle(''); setNewDealId('') }}
              className="px-3 py-2 text-sm rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create
            </button>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      {!loading && projects.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search RFPs by title or customer..."
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-indigo-500 transition-colors"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={stageFilter}
            onChange={e => setStageFilter(e.target.value as RfpStage | 'all')}
            className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 transition-colors"
          >
            <option value="all">All Stages</option>
            {RFP_STAGES.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Projects list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16">
          <FileCheck size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <p className="text-sm text-[var(--text-muted)]">No RFP projects yet. Create one to get started.</p>
        </div>
      ) : (() => {
        const query = searchQuery.toLowerCase()
        const filtered = projects.filter(p => {
          const matchesSearch = !query
            || p.title.toLowerCase().includes(query)
            || (p.customer_name || '').toLowerCase().includes(query)
          const matchesStage = stageFilter === 'all' || (p.stage || 'pre_questions') === stageFilter
          return matchesSearch && matchesStage
        })

        if (filtered.length === 0) {
          return (
            <div className="text-center py-12">
              <Search size={24} className="mx-auto mb-2 text-[var(--text-muted)]" />
              <p className="text-sm text-[var(--text-muted)]">No RFPs match your search.</p>
            </div>
          )
        }

        return (
          <div className="space-y-3">
            {filtered.map(project => {
              const cl = { ...EMPTY_CHECKLIST, ...project.qualification_checklist }
              const qualProgress = getQualificationProgress(project)
              const answerProgress = project.total_requirements > 0
                ? Math.round((project.answered_count / project.total_requirements) * 100)
                : 0
              const stageCfg = STAGE_CONFIG[project.stage || 'pre_questions']

              // Derive the accent border color class from stage
              const stageBorderColor = {
                'pre_questions': 'border-l-gray-500',
                'questions_submitted': 'border-l-blue-500',
                'response_drafting': 'border-l-indigo-500',
                'submitted': 'border-l-violet-500',
                'under_review': 'border-l-amber-500',
                'preferred': 'border-l-emerald-500',
                'closed_won': 'border-l-green-500',
                'closed_lost': 'border-l-red-500',
              }[project.stage || 'pre_questions'] || 'border-l-gray-500'

              return (
                <div
                  key={project.id}
                  onClick={() => selectProject(project)}
                  className={`rounded-xl border border-[var(--border-color)] border-l-[3px] ${stageBorderColor} bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer overflow-hidden`}
                >
                  <div className="p-4">
                    {/* Top row: title, stage, actions */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-[var(--text-primary)] truncate">{project.title}</span>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${stageCfg.bg} ${stageCfg.color} border border-current/20`}>
                            Stage: {stageCfg.label}
                          </span>
                          {project.response_due_date && (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                              <Calendar size={10} className="text-red-400" />
                              Due {formatShortDate(project.response_due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <button
                          onClick={e => handleDelete(project.id, e)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                        >
                          <Trash2 size={13} />
                        </button>
                        <ChevronRight size={14} className="text-[var(--text-muted)]" />
                      </div>
                    </div>

                    {/* Requirements count */}
                    {project.total_requirements > 0 && (
                      <div className="text-[11px] text-[var(--text-muted)] mb-3">
                        {project.answered_count}/{project.total_requirements} answered
                      </div>
                    )}

                    {/* Product pills */}
                    {(cl.products || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(cl.products || []).map(key => {
                          const p = RFP_PRODUCTS.find(rp => rp.key === key)
                          return p ? (
                            <span key={key} className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-indigo-500/10 text-indigo-400">
                              {p.label}
                            </span>
                          ) : null
                        })}
                      </div>
                    )}

                    {/* Progress bars */}
                    <div className="flex items-center gap-4">
                      {/* Qualification progress */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-[var(--text-muted)]">Qualification</span>
                          <span className={`text-[10px] font-medium ${qualProgress.percent === 100 ? 'text-green-400' : 'text-[var(--text-muted)]'}`}>
                            {qualProgress.percent}%
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-[var(--bg-primary)] overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${qualProgress.percent === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                            style={{ width: `${qualProgress.percent}%` }}
                          />
                        </div>
                      </div>

                      {/* Response progress */}
                      {project.total_requirements > 0 && (
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-[var(--text-muted)]">Responses</span>
                            <span className={`text-[10px] font-medium ${answerProgress === 100 ? 'text-green-400' : 'text-[var(--text-muted)]'}`}>
                              {answerProgress}%
                            </span>
                          </div>
                          <div className="h-1 rounded-full bg-[var(--bg-primary)] overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${answerProgress === 100 ? 'bg-green-500' : 'bg-violet-500'}`}
                              style={{ width: `${answerProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })()}

      <QualificationGuide
        open={showGuide}
        onClose={() => setShowGuide(false)}
        checklist={guideChecklist}
        onChecklistChange={setGuideChecklist}
      />

      <RfpSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  )
}
